import React, { useState } from 'react';

export default function Step2Factors({ stepIndicator, factors, setFactors, onBack, onNext, hpTheme = false }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newFactor, setNewFactor] = useState({ name: '', description: '', type: 'subjective', weight: 5 });

  const totalWeight = factors.filter(f => f.enabled).reduce((s, f) => s + f.weight, 0);

  const updateFactor = (id, key, value) => {
    setFactors(factors.map(f => f.id === id ? { ...f, [key]: value } : f));
  };

  const toggleFactor = (id) => {
    setFactors(factors.map(f => f.id === id ? { ...f, enabled: !f.enabled } : f));
  };

  const addCustomFactor = () => {
    if (!newFactor.name.trim()) return;
    const id = `custom_${Date.now()}`;
    setFactors([...factors, { ...newFactor, id, name: newFactor.name.trim(), enabled: true }]);
    setNewFactor({ name: '', description: '', type: 'subjective', weight: 5 });
    setShowAddForm(false);
  };

  const removeFactor = (id) => {
    setFactors(factors.filter(f => f.id !== id));
  };

  const enabledCount = factors.filter(f => f.enabled).length;

  return (
    <div className="card">
      {stepIndicator}
      <div className="step-header">
        <h2>{hpTheme ? 'Tell Me What Matters Most' : 'Step 2 — What Matters to You?'}</h2>
        <p>
          {hpTheme
            ? "Now then, tell me what I ought to prize. Weigh each trait from 1 to 10, and silence any that don't belong in this particular sorting."
            : "Rate the importance of each factor on a scale of 1–10. Toggle off factors that don't apply. These weights determine how much each dimension influences your final score."}
        </p>
      </div>

      {!hpTheme && (
        <div className="notice notice-info mb-4">
          <strong>Total weight:</strong> {totalWeight} points across {enabledCount} factor{enabledCount !== 1 ? 's' : ''}.
          Each factor's percentage contribution is shown below.
        </div>
      )}

      {factors.map(factor => {
        const pct = totalWeight > 0 && factor.enabled
          ? ((factor.weight / totalWeight) * 100).toFixed(1)
          : '—';
        return (
          <div key={factor.id} className={`factor-row ${!factor.enabled ? 'disabled' : ''}`}>
            <button
              className={`factor-toggle ${factor.enabled ? 'on' : ''}`}
              onClick={() => toggleFactor(factor.id)}
              title={factor.enabled ? (hpTheme ? 'Set this aside' : 'Disable factor') : (hpTheme ? 'Bring this into judgment' : 'Enable factor')}
            />
            <div className="factor-info">
              <div className="factor-name">
                {factor.name}
                <span className={`badge badge-${factor.type}`}>
                  {hpTheme
                    ? factor.type === 'subjective'
                      ? 'instinct'
                      : 'record'
                    : factor.type}
                </span>
              </div>
              <div className="factor-desc">{factor.description}</div>
            </div>
            <div className="factor-weight-control">
              <div className="factor-weight-header">
                <span>{hpTheme ? 'Importance' : 'Importance'}</span>
                <div className="factor-weight-stats">
                  <span className="factor-weight-value">{factor.weight}</span>
                  <span className="factor-weight-pct">({pct}%)</span>
                </div>
              </div>
              <input
                type="range"
                min="1"
                max="10"
                value={factor.weight}
                onChange={e => updateFactor(factor.id, 'weight', parseInt(e.target.value))}
                disabled={!factor.enabled}
                className="weight-slider"
              />
            </div>
            {factor.id.startsWith('custom_') && (
              <button
                className="btn btn-danger"
                style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
                onClick={() => removeFactor(factor.id)}
                title={hpTheme ? 'Banish this measure' : 'Remove factor'}
              >
                ✕
              </button>
            )}
          </div>
        );
      })}

      {!showAddForm ? (
        <button
          className="btn btn-ghost"
          style={{ marginTop: '0.5rem', width: '100%' }}
          onClick={() => setShowAddForm(true)}
        >
          {hpTheme ? '+ Add Another Measure' : '+ Add Custom Factor'}
        </button>
      ) : (
        <div className="add-factor-form">
          <h4>{hpTheme ? 'A New Measure For My Judgment' : 'New Custom Factor'}</h4>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{hpTheme ? 'Measure Name *' : 'Factor Name *'}</label>
              <input
                className="input"
                placeholder={hpTheme ? 'e.g. Spirit of Adventure' : 'e.g. Research Opportunities'}
                value={newFactor.name}
                onChange={e => setNewFactor({ ...newFactor, name: e.target.value })}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label className="form-label">{hpTheme ? 'Kind Of Measure' : 'Type'}</label>
              <select
                className="select"
                value={newFactor.type}
                onChange={e => setNewFactor({ ...newFactor, type: e.target.value })}
              >
                <option value="subjective">{hpTheme ? 'Instinctive (you judge it)' : 'Subjective (you rate it)'}</option>
                <option value="objective">{hpTheme ? 'Documented (data-based)' : 'Objective (data-based)'}</option>
              </select>
            </div>
          </div>
          <div className="form-group mb-3">
            <label className="form-label">{hpTheme ? 'Description (if you must)' : 'Description (optional)'}</label>
            <input
              className="input"
              placeholder={hpTheme ? 'A brief note on what I should be weighing...' : 'Brief description of what this measures...'}
              value={newFactor.description}
              onChange={e => setNewFactor({ ...newFactor, description: e.target.value })}
            />
          </div>
          <div className="form-group mb-3">
            <label className="form-label">{hpTheme ? `Importance To The Hat: ${newFactor.weight}/10` : `Importance: ${newFactor.weight}/10`}</label>
            <input
              type="range"
              min="1"
              max="10"
              value={newFactor.weight}
              onChange={e => setNewFactor({ ...newFactor, weight: parseInt(e.target.value) })}
              className="weight-slider"
            />
          </div>
          <div className="flex gap-2">
            <button
              className="btn btn-primary"
              onClick={addCustomFactor}
              disabled={!newFactor.name.trim()}
            >
              {hpTheme ? 'Add Measure' : 'Add Factor'}
            </button>
            <button className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
              {hpTheme ? 'Never Mind' : 'Cancel'}
            </button>
          </div>
        </div>
      )}

      <div className="step-footer">
        <button className="btn btn-secondary" onClick={onBack}>{hpTheme ? '← Back To The Names' : '← Back'}</button>
        <button
          className="btn btn-primary"
          onClick={onNext}
          disabled={enabledCount === 0}
        >
          {hpTheme ? 'On To Your Instincts →' : 'Continue to Your Ratings →'}
        </button>
      </div>
    </div>
  );
}
